/**
 * Classification Registry -- single source of truth for product classification
 * metadata (sector names, sorting orders, services/other categories, and
 * the GraphQL query document used to fetch each classification).
 */

import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { ProductClass } from "../types/enums";

// GraphQL query documents
import getHs92ProductsMetadata from "../graphql/queries/getHs92ProductsMetadata";
import getHs12ProductsMetadata from "../graphql/queries/getHs12ProductsMetadata";
import getHs22ProductsMetadata from "../graphql/queries/getHs22ProductsMetadata";
import getSitcProductsMetadata from "../graphql/queries/getSitcProductsMetadata";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SectorEntry {
  /** e.g. "product-HS92-1" */
  id: string;
  /** e.g. "Textiles" */
  name: string;
}

export interface ClassificationDescriptor {
  key: ProductClass;
  /** The GraphQL query document for fetching this classification's metadata. */
  queryDocument: TypedDocumentNode<any, any>;
  /** Ordered list of sector entries (id + display name). */
  sectorEntries: SectorEntry[];
  /** Product ID of the "Services" sector (e.g. "product-HS92-14"). */
  servicesCategory: string;
  /** Product ID of the "Other" sector, if applicable (e.g. "product-HS92-10"). */
  otherCategory?: string;
  /** Category IDs in the order they should appear when sorting chart stacks. */
  sortingOrder: string[];
  /** Whether this classification has six-digit product codes. */
  hasSixDigit: boolean;
}

// ---------------------------------------------------------------------------
// Registry data
// ---------------------------------------------------------------------------

const hs92Descriptor: ClassificationDescriptor = {
  key: ProductClass.HS92Products,
  queryDocument: getHs92ProductsMetadata as TypedDocumentNode<any, any>,
  sectorEntries: [
    { id: "product-HS92-1", name: "Textiles" },
    { id: "product-HS92-2", name: "Agriculture" },
    { id: "product-HS92-3", name: "Stone" },
    { id: "product-HS92-4", name: "Minerals" },
    { id: "product-HS92-5", name: "Metals" },
    { id: "product-HS92-6", name: "Chemicals" },
    { id: "product-HS92-7", name: "Vehicles" },
    { id: "product-HS92-8", name: "Machinery" },
    { id: "product-HS92-9", name: "Electronics" },
    { id: "product-HS92-10", name: "Other" },
    { id: "product-HS92-14", name: "Services" },
  ],
  servicesCategory: "product-HS92-14",
  otherCategory: "product-HS92-10",
  sortingOrder: [
    "product-HS92-1",
    "product-HS92-2",
    "product-HS92-3",
    "product-HS92-4",
    "product-HS92-5",
    "product-HS92-6",
    "product-HS92-7",
    "product-HS92-8",
    "product-HS92-9",
    "product-HS92-10",
    "product-HS92-14",
  ],
  hasSixDigit: true,
};

const hs12Descriptor: ClassificationDescriptor = {
  key: ProductClass.HS12Products,
  queryDocument: getHs12ProductsMetadata as TypedDocumentNode<any, any>,
  sectorEntries: [
    { id: "product-HS12-1", name: "Textiles" },
    { id: "product-HS12-2", name: "Agriculture" },
    { id: "product-HS12-3", name: "Stone" },
    { id: "product-HS12-4", name: "Minerals" },
    { id: "product-HS12-5", name: "Metals" },
    { id: "product-HS12-6", name: "Chemicals" },
    { id: "product-HS12-7", name: "Vehicles" },
    { id: "product-HS12-8", name: "Machinery" },
    { id: "product-HS12-9", name: "Electronics" },
    { id: "product-HS12-10", name: "Other" },
    { id: "product-HS12-14", name: "Services" },
  ],
  servicesCategory: "product-HS12-14",
  otherCategory: "product-HS12-10",
  sortingOrder: [
    "product-HS12-1",
    "product-HS12-2",
    "product-HS12-3",
    "product-HS12-4",
    "product-HS12-5",
    "product-HS12-6",
    "product-HS12-7",
    "product-HS12-8",
    "product-HS12-9",
    "product-HS12-10",
    "product-HS12-14",
  ],
  hasSixDigit: true,
};

const hs22Descriptor: ClassificationDescriptor = {
  key: ProductClass.HS22Products,
  queryDocument: getHs22ProductsMetadata as TypedDocumentNode<any, any>,
  sectorEntries: [
    { id: "product-HS22-1", name: "Textiles" },
    { id: "product-HS22-2", name: "Agriculture" },
    { id: "product-HS22-3", name: "Stone" },
    { id: "product-HS22-4", name: "Minerals" },
    { id: "product-HS22-5", name: "Metals" },
    { id: "product-HS22-6", name: "Chemicals" },
    { id: "product-HS22-7", name: "Vehicles" },
    { id: "product-HS22-8", name: "Machinery" },
    { id: "product-HS22-9", name: "Electronics" },
    { id: "product-HS22-10", name: "Other" },
    { id: "product-HS22-14", name: "Services" },
  ],
  servicesCategory: "product-HS22-14",
  otherCategory: "product-HS22-10",
  sortingOrder: [
    "product-HS22-1",
    "product-HS22-2",
    "product-HS22-3",
    "product-HS22-4",
    "product-HS22-5",
    "product-HS22-6",
    "product-HS22-7",
    "product-HS22-8",
    "product-HS22-9",
    "product-HS22-10",
    "product-HS22-14",
  ],
  hasSixDigit: true,
};

const sitcDescriptor: ClassificationDescriptor = {
  key: ProductClass.SITCProducts,
  queryDocument: getSitcProductsMetadata as TypedDocumentNode<any, any>,
  sectorEntries: [
    { id: "product-SITC-1", name: "Food" },
    { id: "product-SITC-2", name: "Beverages" },
    { id: "product-SITC-3", name: "Crude Materials" },
    { id: "product-SITC-4", name: "Fuels" },
    { id: "product-SITC-5", name: "Vegetable Oils" },
    { id: "product-SITC-6", name: "Chemicals" },
    { id: "product-SITC-7", name: "Material Manufacturers" },
    { id: "product-SITC-8", name: "Machinery & Vehicles" },
    { id: "product-SITC-9", name: "Other Manufacturers" },
    { id: "product-SITC-10", name: "Unspecified" },
    { id: "product-SITC-14", name: "Services" },
  ],
  servicesCategory: "product-SITC-14",
  otherCategory: undefined,
  sortingOrder: [
    "product-SITC-1",
    "product-SITC-2",
    "product-SITC-3",
    "product-SITC-4",
    "product-SITC-5",
    "product-SITC-6",
    "product-SITC-7",
    "product-SITC-8",
    "product-SITC-9",
    "product-SITC-10",
    "product-SITC-14",
  ],
  hasSixDigit: false,
};

// ---------------------------------------------------------------------------
// Internal map
// ---------------------------------------------------------------------------

const registry = new Map<ProductClass, ClassificationDescriptor>([
  [ProductClass.HS92Products, hs92Descriptor],
  [ProductClass.HS12Products, hs12Descriptor],
  [ProductClass.HS22Products, hs22Descriptor],
  [ProductClass.SITCProducts, sitcDescriptor],
]);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Return the full descriptor for a given product classification.
 * Throws if the classification is unknown.
 */
export function getClassification(pc: ProductClass): ClassificationDescriptor {
  const desc = registry.get(pc);
  if (!desc) {
    throw new Error(`Unknown product classification: ${pc}`);
  }
  return desc;
}

/**
 * Build a Map<sectorId, displayName> for the given classification.
 * The map is built fresh each call; callers should cache if needed.
 */
export function getClassificationNamesMap(pc: ProductClass): Map<string, string> {
  const desc = getClassification(pc);
  return new Map(desc.sectorEntries.map((e) => [e.id, e.name]));
}

/**
 * Return the category sorting order for a given classification.
 */
export function getClassificationSortingOrder(pc: ProductClass): string[] {
  return getClassification(pc).sortingOrder;
}

/**
 * Return the services category ID for a given classification,
 * or `undefined` if the classification does not define one.
 */
export function getClassificationServicesCategory(pc: ProductClass): string | undefined {
  return getClassification(pc).servicesCategory;
}

/**
 * Return the "other" category ID for a given classification,
 * or `undefined` if the classification does not define one.
 */
export function getClassificationOtherCategory(pc: ProductClass): string | undefined {
  return getClassification(pc).otherCategory;
}

/**
 * Return the GraphQL query document for fetching product metadata
 * for a given classification.
 */
export function getClassificationQueryDocument(pc: ProductClass): TypedDocumentNode<any, any> {
  return getClassification(pc).queryDocument;
}

/**
 * Return all registered classifications.
 */
export function getAllClassifications(): ClassificationDescriptor[] {
  return Array.from(registry.values());
}
